import { Container, Grid } from "@mui/material";
import { OrderList } from "components/order/order-list";
import "leaflet/dist/leaflet.css";
import { EmployeeList } from "components/employee/employee-list";
import { MapBasic } from "components/map-basic";
import { useOrderApi } from "api/OrderApi/order-api";
import { ApiResultStatus, OrderDeliveryStatus } from "utils/enums";
import { Suspense, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { IMarker } from "components/interfaces";
import { Subscribe } from "@react-rxjs/core";
import { useEmployeeApi } from "api/EmployeeApi/employee-api";
import { useEmployeeId } from "components/employee/employee-events";
import {
  useOrderEmplyeeChanged,
  useOrderParametersChanged,
} from "components/order/order-events";
import { REFRESH_DATA_INTERVAL_IN_MILISECONDS } from "utils/consts";
import { useTranslation } from "react-i18next";
import { useLocationApi } from "api/LocationApi/location-api";
import { ILocationGetDetailsResult } from "api/LocationApi/interfaces-result";
import { IOrderGetListResult } from "api/OrderApi/interfaces-result";
import { IEmployeeGetListResult } from "api/EmployeeApi/interfaces-result";
import { OrderListFilter } from "components/order/order-list-filter";
import { useDeliveryAreaApi } from "api/DeliveryAreaApi/delivery-area-api";
import { IDeliveryAreaGeoJSONDto } from "api/DeliveryAreaApi/interfaces-result";
import { IResultBaseGeneric } from "api/interfaces";

export const Main: () => JSX.Element = (): JSX.Element => {
  const { getList: getOrderList } = useOrderApi();
  const { getList: getEmployeeList } = useEmployeeApi();
  const { getDetails: getLocationDetails } = useLocationApi();
  const { getGeoJSONList } = useDeliveryAreaApi();
  const [employeeList, setEmployeeList] = useState<IEmployeeGetListResult[]>(
    []
  );
  const [filteredEmployeeList, setFilteredEmployeeList] = useState<
    IEmployeeGetListResult[]
  >([]);
  const [orderList, setOrderList] = useState<IOrderGetListResult[]>([]);
  const [orderMarkerList, setOrderMarkerList] = useState<IMarker<void>[]>([]);
  const [employeeMarkerList, setEmployeeMarkerList] = useState<
    IMarker<IEmployeeGetListResult>[]
  >([]);
  const [locationDetails, setLocationDetails] =
    useState<ILocationGetDetailsResult>();
  const [deliveryAreaGeoJSONList, setDeliveryAreaGeoJSONList] =
    useState<IDeliveryAreaGeoJSONDto[]>();
  const [loadOrderListInterval, setLoadOrderListInterval] =
    useState<NodeJS.Timer>();
  const [loadEmployeeDataInterval, setLoadEmployeeDataInterval] =
    useState<NodeJS.Timer>();
  const [showMap, setShowMap] = useState<boolean>(() => {
    let saved = localStorage.getItem("showMap") ?? "true";
    return saved === "true";
  });
  const [showOnlyActiveEmployees, setShowOnlyActiveEmployees] =
    useState<boolean>(() => {
      let saved = localStorage.getItem("showOnlyActiveEmployees") ?? "false";
      return saved === "true";
    });
  const [showExternalEmployees, setShowExternalEmployees] = useState<boolean>(
    () => {
      let saved = localStorage.getItem("showExternalEmployees") ?? "false";
      return saved === "true";
    }
  );
  const [showWithoutEmployee, setShowWithoutEmployee] = useState<boolean>(
    () => {
      let saved = localStorage.getItem("showWithoutEmployee") ?? "false";
      return saved === "true";
    }
  );
  const [showDeliveredOrder, setShowDeliveredOrder] = useState<boolean>(() => {
    let saved = localStorage.getItem("showDeliveredOrder") ?? "true";
    return saved === "true";
  });
  const { t } = useTranslation(["commonTranslation"]);

  const selectedEmployeeId = useEmployeeId();
  const orderEmployeeChanged = useOrderEmplyeeChanged();
  const orderParametersChanged = useOrderParametersChanged();

  const handleGetLocalizationDetails: () => void = (): void => {
    getLocationDetails().then((response) => {
      if (response?.status === ApiResultStatus.Ok) {
        setLocationDetails(response.data);
      }
    });
  };

  useEffect(() => {
    handleGetLocalizationDetails();
  }, []);

  const loadOrderList: () => void = (): void => {
    getOrderList({
      showDeliveredOrder: showDeliveredOrder,
      showWithoutEmployee: selectedEmployeeId ? false : showWithoutEmployee,
      employeeId: selectedEmployeeId ? selectedEmployeeId : undefined,
    }).then((response) => {
      if (response?.status === ApiResultStatus.Ok) {
        setOrderList(response.data);

        const newOrderMarkerList: IMarker<void>[] = _.map(
          _.filter(response.data, (o) => {
            return !!o.longitude && !!o.latitude;
          }),
          (o) => {
            let color: string = "";
            if (o.deliveryStatus == OrderDeliveryStatus.New) {
              if (o.employeeId) {
                color = "#ffff77";
              } else {
                color = "#004563";
              }
            } else if (
              o.deliveryStatus == OrderDeliveryStatus.ReadyForCollect
            ) {
              if (o.employeeId) {
                color = "#000000";
              } else {
                color = "#f9e04b";
              }
            } else if (o.deliveryStatus == OrderDeliveryStatus.Delivered) {
              color = "#008000";
            } else if (o.deliveryStatus == OrderDeliveryStatus.NoOneOpened) {
              color = "#ff833a";
            } else {
              // OrderDeliveryStatus.ToBeDelivered && o.employeeId
              color = "#f7d60e";
            }

            let newMarker: IMarker<void> = {
              id: o.orderId,
              latitude: o.latitude!,
              longitude: o.longitude!,
              tooltipText: o.orderDailyNumber,
              color: color,
            };
            return newMarker;
          }
        );
        setOrderMarkerList(newOrderMarkerList);
      }
    });
  };

  const prevEmployeeListRef = useRef<IEmployeeGetListResult[]>([]);
  const notificationSound = useRef(new Audio("bell_ring.mp3"));

  const loadEmployeeData: () => void = (): void => {
    getEmployeeList().then(
      (response: IResultBaseGeneric<IEmployeeGetListResult[]>) => {
        if (response?.status === ApiResultStatus.Ok) {
          const prevEmployeeList = prevEmployeeListRef.current;
          const newEmployeeList = response.data;

          const hasReturningStatusChanged = newEmployeeList.some(
            (newEmployee) => {
              const prevEmployee = prevEmployeeList.find(
                (e) => e.id === newEmployee.id
              );
              return (
                prevEmployee &&
                !prevEmployee.isReturning &&
                newEmployee.isReturning
              );
            }
          );

          if (hasReturningStatusChanged) {
            notificationSound.current.play();
          }
          prevEmployeeListRef.current = newEmployeeList;

          setEmployeeList(response.data);

          setFilteredEmployeeList(
            response.data.filter((e) => {
              if (e.isExternal || e.isDeliveryMarket) {
                return showExternalEmployees;
              }
              return !showOnlyActiveEmployees || e.isActive;
            })
          );
        }
      }
    );
  };

  const loadDeliveryAreaGeoJSONList: () => void = (): void => {
    getGeoJSONList().then((response) => {
      if (response?.status === ApiResultStatus.Ok) {
        setDeliveryAreaGeoJSONList(response.data);
      }
    });
  };

  useEffect(() => {
    if (orderEmployeeChanged.orderId > 0) {
      loadOrderList();
    }
  }, [orderEmployeeChanged]);

  useEffect(() => {
    if (orderParametersChanged.orderId > 0) {
      loadOrderList();
    }
  }, [orderParametersChanged]);

  useEffect(() => {
    if (locationDetails) {
      if (locationDetails.windRoseLatitude) {
        loadDeliveryAreaGeoJSONList();
      }

      if (!locationDetails.isAssignNotLoggedDriverEnabled) {
        setShowOnlyActiveEmployees(false);
        localStorage.setItem("showOnlyActiveEmployees", "false");
      }
    }
  }, [locationDetails]);

  useEffect(() => {
    const newEmployeeMarkerList: IMarker<IEmployeeGetListResult>[] = _.map(
      _.filter([...filteredEmployeeList], (e) => {
        return (
          !!e.lastDeviceLocalizationLongitude &&
          !!e.lastDeviceLocalizationLatitude
        );
      }),
      (e) => {
        let newMarker: IMarker<IEmployeeGetListResult> = {
          id: e.id,
          latitude: e.lastDeviceLocalizationLatitude!,
          longitude: e.lastDeviceLocalizationLongitude!,
          color: e.color,
          tooltipText: e.phoneNo,
          data: e,
        };
        return newMarker;
      }
    );
    setEmployeeMarkerList(newEmployeeMarkerList);
  }, [filteredEmployeeList]);

  useEffect(() => {
    // loadOrderList();
    loadEmployeeData();
    // setIntervals();
  }, []);

  useEffect(() => {
    loadOrderList();
    setIntervals();
  }, [
    selectedEmployeeId,
    showDeliveredOrder,
    showWithoutEmployee,
    showOnlyActiveEmployees,
    showExternalEmployees,
  ]);

  useEffect(() => {
    loadEmployeeData();
  }, [showOnlyActiveEmployees, showExternalEmployees]);

  const setIntervals: () => void = (): void => {
    clearInterval(loadOrderListInterval);
    clearInterval(loadEmployeeDataInterval);

    setLoadOrderListInterval(
      setInterval(() => {
        loadOrderList();
      }, REFRESH_DATA_INTERVAL_IN_MILISECONDS)
    );

    setLoadEmployeeDataInterval(
      setInterval(() => {
        loadEmployeeData();
      }, REFRESH_DATA_INTERVAL_IN_MILISECONDS / 4)
    );
  };

  return (
    <Container maxWidth={false}>
      <Subscribe>
        <Suspense fallback={<p>wait</p>}>
          <Grid container spacing={2}>
            <Grid
              container
              item
              xs={showMap ? 6 : 12}
              spacing={1}
              height="95vh"
            >
              <Grid item xs={12}>
                {locationDetails && (
                  <OrderListFilter
                    locationDetails={locationDetails}
                    defaultOrderReadyTimeInMinutes={
                      locationDetails.defaultOrderReadyTimeInMinutes
                    }
                    showDeliveredOrder={showDeliveredOrder}
                    showMap={showMap}
                    showWithoutEmployee={showWithoutEmployee}
                    showOnlyActiveEmployees={showOnlyActiveEmployees}
                    showExternalEmployees={showExternalEmployees}
                    handleShowMapOnClick={(showMap: boolean) => {
                      setShowMap(showMap);
                      localStorage.setItem("showMap", showMap.toString());
                    }}
                    handleSetShowDeliveredOrderOnClick={(
                      isChecked: boolean
                    ) => {
                      setShowDeliveredOrder(isChecked);
                      localStorage.setItem(
                        "showDeliveredOrder",
                        isChecked.toString()
                      );
                    }}
                    handleSetShowWithoutEmployeeOnClick={(
                      isChecked: boolean
                    ) => {
                      setShowWithoutEmployee(isChecked);
                      localStorage.setItem(
                        "showWithoutEmployee",
                        isChecked.toString()
                      );
                    }}
                    handleSetDefaultOrderReadyTimeInMinutesOnSave={(
                      defaultOrderReadyTimeInMinutes: number
                    ) => {
                      setLocationDetails({
                        ...locationDetails,
                        defaultOrderReadyTimeInMinutes:
                          defaultOrderReadyTimeInMinutes,
                      });
                    }}
                    handleShowOnlyActiveEmployeesOnClick={(
                      isChecked: boolean
                    ) => {
                      setShowOnlyActiveEmployees(isChecked);
                      localStorage.setItem(
                        "showOnlyActiveEmployees",
                        isChecked.toString()
                      );
                    }}
                    handleShowExternalEmployeesOnClick={(
                      isChecked: boolean
                    ) => {
                      setShowExternalEmployees(isChecked);
                      localStorage.setItem(
                        "showExternalEmployees",
                        isChecked.toString()
                      );
                    }}
                  />
                )}
              </Grid>
              <Grid
                item
                xs={showMap ? 12 : 6}
                height={showMap ? "130px" : "calc(98vh - 100px)"}
              >
                {locationDetails && (
                  <EmployeeList
                    employeeList={filteredEmployeeList}
                    itemSize={6} /*{showMap ? 6 : 4}*/
                    isSingleRowMode={showMap}
                    isAssignNotLoggedDriverEnabled={
                      locationDetails.isAssignNotLoggedDriverEnabled
                    }
                  />
                )}
              </Grid>
              <Grid
                item
                xs={showMap ? 12 : 6}
                height={showMap ? "calc(98vh - 225px)" : "calc(98vh - 100px)"}
              >
                {locationDetails && (
                  <OrderList
                    orderList={orderList}
                    employeeList={employeeList}
                    isAssignDriverEnabled={
                      locationDetails.isAssignDriverEnabled
                    }
                    isWindRoseDefined={!!locationDetails.windRoseLatitude}
                    isOrderReadyDateChangeEnabled={locationDetails.isOrderReadyDateChangeEnabled}
                  />
                )}
              </Grid>
            </Grid>
            {showMap && locationDetails && (
              <Grid container item xs={6} height="98vh">
                <Grid item xs={12}>
                  <MapBasic
                    orderMarkerList={orderMarkerList}
                    employeeMarkerList={employeeMarkerList}
                    locationDetails={locationDetails}
                    deliveryAreaGeoJSONList={deliveryAreaGeoJSONList}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Suspense>
      </Subscribe>
    </Container>
  );
};
